import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    ArrowPathIcon,
    ChevronDownIcon,
    WalletIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { EffectiveCapitalSummaryAccounts } from "../../components/effectiveCapital/EffectiveCapitalSummaryAccounts";
import { EffectiveCapitalSummaryTransactions } from "../../components/effectiveCapital/EffectiveCapitalSummaryTransactions";
import { EffectiveCapitalSummaryCashflowExport } from "../../components/effectiveCapital/EffectiveCapitalSummaryCashflowExport";
import { EffectiveCapitalSummaryHistory } from "../../components/effectiveCapital/EffectiveCapitalSummaryHistory";
import { EffectiveCapitalSummaryConnectAccounts } from "../../components/effectiveCapital/EffectiveCapitalSummaryConnectAccounts";
import { EffectiveCapitalSummaryCapitalSummary } from "../../components/effectiveCapital/EffectiveCapitalSummaryCapitalSummary";
import { effectiveCapitalApi } from "../../helpers/effectiveCapitalApi";
import "../../components/effectiveCapital/effectiveCapital.css";
import { LoaderSpinner } from "../../components/LoaderSpinner";
import { BankBalancesContent } from "../../components/effectiveCapital/BankBalancesDrawer";
import { getVisibleEffectiveCapitalTabs } from "./effectiveCapitalTabs";

const EffectiveCapitalSummary = ({
    marginFromSidebar,
    allowedPermissionIds,
}) => {

    const { currentUser } = useSelector((state) => state.auth);
    const tabs = useMemo(
        () => getVisibleEffectiveCapitalTabs(currentUser, allowedPermissionIds),
        [currentUser, allowedPermissionIds]
    );
    const canViewSummary = tabs.some((tab) => tab.value === "capital-summary");

    const [searchParams, setSearchParams] = useSearchParams();
    const defaultTab = tabs.find((tab) => tab.value === "capital-summary")?.value || tabs[0]?.value || "transactions";
    const requestedTab = searchParams?.get("tab") || defaultTab;
    const activeTab = tabs.some((t) => t.value === requestedTab) ? requestedTab : defaultTab;
    useEffect(() => {
        if (searchParams.get("tab") === activeTab) return;
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("tab", activeTab);
            return next;
        }, { replace: true });
    }, [activeTab, searchParams, setSearchParams]);
    const handleTabClick = (tab) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("tab", tab);
            return next;
        }, { replace: true });
    };
    const historyEntity = searchParams.get("entity") === "kings_holding" ? "kings_holding" : "kings_loot";
    const handleHistoryEntity = (entity) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set("tab", "history");
            next.set("entity", entity);
            return next;
        }, { replace: true });
    };

    const [menuOpen, setMenuOpen] = useState(false);
    const [tabLoading, setTabLoading] = useState(false);
    const menuRef = useRef(null);
    const activeTabLabel = activeTab === "history"
        ? `History — ${historyEntity === "kings_holding" ? "Kings Holding" : "Kings Loot"}`
        : (tabs.find((t) => t.value === activeTab)?.label ?? "Menu");
    const sidebarOffsetPx = Number(marginFromSidebar) || 0;

    const handleLoadingChange = useCallback((loading) => {
        setTabLoading(Boolean(loading));
    }, []);

    useEffect(() => {
        if (!menuOpen) return undefined;
        const onPointerDown = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        const onKeyDown = (e) => {
            if (e.key === "Escape") setMenuOpen(false);
        };
        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [menuOpen]);

    const [pdfLoading, setPdfLoading] = useState(false);
    const handleExportPdf = async (asOfDate) => {
        setPdfLoading(true);
        try {
            const blob = await effectiveCapitalApi.exportCapitalSummaryPdf(asOfDate);
            if (blob?.type && blob.type.includes('application/json')) {
                const text = await blob.text();
                const parsed = JSON.parse(text);
                throw new Error(parsed.error || parsed.details || 'PDF generation failed');
            }
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const dateStr = (asOfDate ? new Date(`${asOfDate}T12:00:00`) : new Date())
                .toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" })
                .replace(/\//g, ".");
            a.download = `CapitalSummary_${dateStr}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Capital Summary PDF exported", { position: "bottom-right" });
        } catch (error) {
            let message = error?.message || "PDF generation failed";
            const data = error?.response?.data;
            if (data instanceof Blob) {
                try {
                    const parsed = JSON.parse(await data.text());
                    message = parsed.error || parsed.details || message;
                } catch {
                    // keep message
                }
            } else if (data?.error) {
                message = data.error;
            }
            toast.error(message, { position: "bottom-right" });
        } finally {
            setPdfLoading(false);
        }
    };

    const [menuSyncing, setMenuSyncing] = useState(false);
    const handleMenuSync = async () => {
        setMenuSyncing(true);
        try {
            const result = await effectiveCapitalApi.syncBalances();
            toast.success(result?.message || "Balances synced", { position: "bottom-right" });
        } catch (error) {
            toast.error(error?.response?.data?.error || "Balance sync failed", { position: "bottom-right" });
        } finally {
            setMenuSyncing(false);
        }
    };

    const menuNode = (
                    <div className="relative shrink-0" ref={menuRef}>
                        <button
                            type="button"
                            onClick={() => setMenuOpen((open) => !open)}
                            className="inline-flex items-center gap-1.5 text-sm font-bold uppercase tracking-widest text-gray-900 hover:text-gray-600"
                            aria-expanded={menuOpen}
                            aria-haspopup="true"
                            aria-label="Open navigation menu"
                        >
                            <span className="truncate">{activeTabLabel}</span>
                            {tabLoading && (
                                <LoaderSpinner height="16" width="16" colorSpinner="black" border="2" />
                            )}
                            <ChevronDownIcon className={`h-3.5 w-3.5 shrink-0 text-gray-500 transition-transform duration-150 ${menuOpen ? "rotate-180" : ""}`} />
                        </button>
                        {menuOpen && (
                            <div
                                className="absolute left-0 top-full z-40 mt-1 min-w-[220px] overflow-visible rounded-md border border-neutral-700 bg-neutral-900 py-1 shadow-xl"
                                style={{
                                    width: `min(16rem, calc(100vw - ${sidebarOffsetPx}px - 2rem))`,
                                }}
                            >
                                {tabs.map((tab) => {
                                    const isActive = activeTab === tab.value;
                                    const isTabLoading = isActive && tabLoading;
                                    const Icon = tab.icon;
                                    if (tab.value === "history") {
                                        return (
                                            <div key={tab.value} className="group relative">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        handleHistoryEntity("kings_loot");
                                                        setMenuOpen(false);
                                                    }}
                                                    className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] ${
                                                        isActive
                                                            ? "bg-neutral-800 font-semibold text-white"
                                                            : "text-neutral-400 hover:bg-neutral-800/60 hover:text-neutral-200"
                                                    } first:rounded-t-md last:rounded-b-md`}
                                                >
                                                    <Icon className={`h-3.5 w-3.5 shrink-0 ${isActive ? "text-white" : "text-neutral-500"}`} />
                                                    <span>History</span>
                                                    <span className="ml-auto text-neutral-500">›</span>
                                                </button>
                                                <div className="invisible absolute left-full top-0 z-50 ml-1 w-44 rounded-md border border-neutral-700 bg-neutral-900 py-1 opacity-0 shadow-xl group-hover:visible group-focus-within:visible group-hover:opacity-100 group-focus-within:opacity-100">
                                                    {["kings_loot", "kings_holding"].map((entity) => (
                                                        <button
                                                            key={entity}
                                                            type="button"
                                                            onClick={() => {
                                                                handleHistoryEntity(entity);
                                                                setMenuOpen(false);
                                                            }}
                                                            className="block w-full px-3 py-2 text-left text-[13px] text-neutral-400 hover:bg-neutral-800/60 hover:text-neutral-200 first:rounded-t-md last:rounded-b-md"
                                                        >
                                                            {entity === "kings_loot" ? "Kings Loot" : "Kings Holding"}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    }
                                    return (
                                        <button
                                            key={tab.value}
                                            type="button"
                                            onClick={() => {
                                                handleTabClick(tab.value);
                                                setMenuOpen(false);
                                            }}
                                            className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] transition-colors ${
                                                isActive
                                                    ? "bg-neutral-800 font-semibold text-white"
                                                    : "text-neutral-400 hover:bg-neutral-800/60 hover:text-neutral-200"
                                            } first:rounded-t-md last:rounded-b-md`}
                                        >
                                            <Icon className={`h-3.5 w-3.5 shrink-0 ${isActive ? "text-white" : "text-neutral-500"}`} />
                                            {!isTabLoading && <span>{tab.label}</span>}
                                            {isTabLoading && (
                                                <LoaderSpinner height="16" width="16" colorSpinner="white" border="2" />
                                            )}
                                        </button>
                                    );
                                })}
                                {canViewSummary && (
                                    <>
                                        <div className="my-1 border-t border-neutral-700" />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                handleTabClick("bank-balances");
                                                setMenuOpen(false);
                                            }}
                                            className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] text-neutral-400 transition-colors hover:bg-neutral-800/60 hover:text-neutral-200 first:rounded-t-md last:rounded-b-md"
                                        >
                                            <WalletIcon className="h-3.5 w-3.5 shrink-0 text-neutral-500" />
                                            Bank Balances
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                handleMenuSync();
                                                setMenuOpen(false);
                                            }}
                                            disabled={menuSyncing}
                                            className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px] text-neutral-400 transition-colors hover:bg-neutral-800/60 hover:text-neutral-200 disabled:opacity-50 first:rounded-t-md last:rounded-b-md"
                                        >
                                            <ArrowPathIcon className={`h-3.5 w-3.5 shrink-0 text-neutral-500 ${menuSyncing ? "animate-spin" : ""}`} />
                                            {menuSyncing ? "Syncing..." : "Sync Balances"}
                                        </button>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
    );

    return (
        <main className="effective-capital w-full h-full px-3 sm:px-6 lg:px-8">
            <div className="space-y-4 my-4">
                <section className="w-full h-full">
                    {activeTab === "capital-summary" && (
                        <EffectiveCapitalSummaryCapitalSummary
                            onNavigateTab={handleTabClick}
                            onLoadingChange={handleLoadingChange}
                            viewSwitcher={menuNode}
                            onExportPdf={canViewSummary ? handleExportPdf : undefined}
                            pdfLoading={pdfLoading}
                        />
                    )}
                    {activeTab === "accounts" && <EffectiveCapitalSummaryAccounts onNavigateTab={handleTabClick} onLoadingChange={handleLoadingChange} viewSwitcher={menuNode} />}
                    {activeTab === "transactions" && <EffectiveCapitalSummaryTransactions onNavigateTab={handleTabClick} onLoadingChange={handleLoadingChange} viewSwitcher={menuNode} />}
                    {activeTab === "cashflow-export" && <EffectiveCapitalSummaryCashflowExport onNavigateTab={handleTabClick} onLoadingChange={handleLoadingChange} viewSwitcher={menuNode} />}
                    {activeTab === "history" && <EffectiveCapitalSummaryHistory entity={historyEntity} onLoadingChange={handleLoadingChange} viewSwitcher={menuNode} />}
                    {activeTab === "connect-accounts" && <EffectiveCapitalSummaryConnectAccounts onLoadingChange={handleLoadingChange} viewSwitcher={menuNode} />}
                    {activeTab === "bank-balances" && (
                        <BankBalancesContent open onLoadingChange={handleLoadingChange} viewSwitcher={menuNode} />
                    )}
                </section>
            </div>
        </main>
    )
};

export default EffectiveCapitalSummary;
